import { Link } from 'react-router-dom';
import { Card, Button, cn } from '@mlm/ui';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';

export function KpiCard({
  label,
  value,
  trend,
  hint,
  emphasis = 'default',
  icon: Icon,
  cta,
}: {
  label: string;
  value: string;
  trend?: number | null;
  hint?: string;
  emphasis?: 'default' | 'primary';
  icon?: LucideIcon;
  cta?: { to: string; label: string; icon?: LucideIcon };
}) {
  const hasTrend = typeof trend === 'number';
  const up = hasTrend && trend! >= 0;
  const isPrimary = emphasis === 'primary';
  const isZero = /^0?(\.0+)?$/.test(value.replace(/[^0-9.]/g, ''));
  const CtaIcon = cta?.icon;
  return (
    <Card
      className={cn(
        'p-4',
        isPrimary && 'border-transparent bg-primary text-primary-foreground shadow-elevated',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            'text-xs font-medium uppercase tracking-wide',
            isPrimary ? 'text-primary-foreground/80' : 'text-foreground/70',
          )}
        >
          {label}
        </div>
        {Icon && (
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              isPrimary ? 'text-primary-foreground/70' : 'text-muted-foreground/60',
            )}
          />
        )}
      </div>
      <div
        className={cn(
          'mt-1.5 tabular-nums',
          isPrimary ? 'text-kpi' : 'text-xl font-semibold',
          !isPrimary && isZero && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
      {(hasTrend || hint) && (
        <div className="mt-1 flex items-center gap-1 text-xs">
          {hasTrend && (
            <span
              className={cn(
                'inline-flex items-center font-medium tabular-nums',
                up ? 'text-credit' : 'text-debit',
              )}
            >
              {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {Math.abs(trend!).toFixed(1)}%
            </span>
          )}
          {hint && (
            <span className={isPrimary ? 'text-primary-foreground/70' : 'text-muted-foreground'}>
              {hint}
            </span>
          )}
        </div>
      )}
      {cta && (
        <Button
          asChild
          size="sm"
          variant={isPrimary ? 'secondary' : 'default'}
          className="mt-3 w-full"
        >
          <Link to={cta.to}>
            {CtaIcon && <CtaIcon className="h-4 w-4" />}
            {cta.label}
          </Link>
        </Button>
      )}
    </Card>
  );
}
